import { NgFor } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CUSTOM_ICON_TYPE } from '@shared/models';
import { IconModule } from '@spartacus/storefront';

/**
 * Компонент рейтинга
 * Может использоваться как обычный компонент, так и как стандартное поле в форме
 *
 * Пример использования в форме:
 * <ui-star-rating
 *  formControlName="rate"
 *  [required]="true">
 * </ui-star-rating>
 *
 */

@Component({
  selector: 'ui-star-rating',
  templateUrl: './custom-star-rating.component.html',
  styleUrls: ['./custom-star-rating.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgFor, IconModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: CustomStarRatingComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: CustomStarRatingComponent,
    },
  ],
})
export class CustomStarRatingComponent implements ControlValueAccessor, Validator {
  /**
   * Значение по умолчанию, если не передано значение рейтинга
   */
  readonly defaultValue = 0;

  /**
   * Значение рейтинга
   */
  @Input() rating = this.defaultValue;

  /**
   * Стандарный атрибут форм контрола
   * Если передать true, то будет отключено взаимодействие с пользователем
   */
  @Input() disabled = false;

  /**
   * Стандарный атрибут форм контрола
   * Если передать true, то при валидации будет возвращать ошибку если не выбрано значение
   */
  @Input() required = false;

  /**
   * Иконка отображающаяся в рейтинге
   */
  iconType = CUSTOM_ICON_TYPE.STAR;

  /**
   * Выбранное пользователем значение
   */
  savedValue = this.rating || this.defaultValue;

  /**
   * Статус взаимодействия с пользователем
   */
  touched = false;

  uid: number;

  /**
   * Коллбек для взаимодействия с формой
   */
  onChange: (value: number) => void;

  /**
   * Коллбек для взаимодействия с формой
   */
  onTouched: () => void;

  constructor() {
    this.uid = Math.random();
  }

  /**
   * Вызывается контролом при первом взаимодействии пользователя с контролом
   */
  markAsTouched(): void {
    if (this.touched) {
      return;
    }

    this.touched = true;
    this.onTouched();
  }

  /**
   * Вызывается контролом при наведении мыши на контрол
   * Изменяет текущее отображаемое значение в рейтинге
   */
  setRate(value: number): void {
    if (this.disabled) {
      return;
    }

    this.rating = value;
  }

  /**
   * Вызывается контролом при клике пользователем на рейтинг
   */
  onClickRating(value: number): void {
    if (this.disabled) {
      return;
    }

    this.savedValue = value;
    this.onChange(value);
    this.markAsTouched();
  }

  /**
   * Вызывается формой при регистрации коллбека onChange
   */
  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  /**
   * Вызывается формой при регистрации коллбека onTouched
   */
  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  /**
   * Вызывается формой чтобы записать значение контрола
   */
  writeValue(value: number): void {
    this.rating = value;
    this.savedValue = value;
  }

  /**
   * Вызывается формой чтобы перевести статус контрола в значение disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getOffset(i: number, rating: number): number {
    const diff = rating - (i - 1);

    if (diff > 1) {
      return 100;
    }
    if (diff < 0) {
      return 0;
    }

    return diff * 100;
  }

  getId(i: number, isUrl: boolean): string {
    return isUrl ? 'url(#half' + i + this.uid + ')' : 'half' + i + this.uid;
  }

  /**
   * Вызывается формой при прохождении проверки на валидность
   */
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required && (!control.value || control.value < 1 || control.value > 5)) {
      return { required: true };
    }

    return null;
  }
}
